// src/pages/admin/Products.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiUpload,
  FiGrid,
  FiList,
  FiX,
  FiSave,
  FiPackage,
  FiImage
} from 'react-icons/fi';

const Products = () => {
  // State
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // View mode - localStorage-с уншиж эхлүүлэх
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('productsViewMode') || 'list';
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Selected product
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    barcode: '',
    name: '',
    category_id: '',
    unit: 'ШИРХЭГ',
    retail_price: '',
    wholesale_price: '',
    wholesale_quantity: '',
    cost_price: '',
    initial_stock: 0,
    supplier: '',
    description: ''
  });

  // Images
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  // Import file
  const [importFile, setImportFile] = useState(null);

  // ========================================
  // FETCH DATA
  // ========================================
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [pagination.page, pagination.limit, searchQuery, selectedCategory]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/products', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          category_id: selectedCategory
        }
      });

      if (response.data.success) {
        setProducts(response.data.data.products);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Fetch products error:', error);
      toast.error('Бараа татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/products/categories/list');
      if (response.data.success) {
        setCategories(response.data.data.categories);
      }
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  };

  // ========================================
  // IMAGE HANDLING
// ========================================
const handleImageSelect = async (e) => {
  const files = Array.from(e.target.files);
  
  console.log('🔍 ===== IMAGE SELECT DEBUG =====');
  console.log('📸 Raw files from input:', e.target.files);
  console.log('📸 Files array length:', files.length);
  console.log('📸 Files details:', files.map(f => ({ 
    name: f.name, 
    size: f.size, 
    type: f.type 
  })));
  
  if (files.length === 0) {
    console.log('⚠️ No files selected');
    toast.warning('Зураг сонгогдсонгүй');
    return;
  }
  
  // Хуучин зургуудтай нэмж нийт 3-аас их болохгүй байх
  const totalImages = imagePreview.length + files.length;
  console.log('📊 Current images:', imagePreview.length);
  console.log('📊 New files:', files.length);
  console.log('📊 Total would be:', totalImages);
  
  if (totalImages > 3) {
    toast.warning(`Хамгийн ихдээ 3 зураг оруулна уу. Одоо ${imagePreview.length} байна, ${3 - imagePreview.length} зураг нэмж болно.`);
    e.target.value = '';
    return;
  }

  // Check file types
  const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
  const invalidTypeFiles = files.filter(file => !validTypes.includes(file.type));
  if (invalidTypeFiles.length > 0) {
    console.log('❌ Invalid type files:', invalidTypeFiles.map(f => ({ name: f.name, type: f.type })));
    toast.warning('Зөвхөн JPG, PNG, WEBP зураг оруулна уу');
    e.target.value = '';
    return;
  }

  console.log('✅ All validations passed');

  // Compress options
  const options = {
    maxSizeMB: 1, // Max 1MB compressed
    maxWidthOrHeight: 1024, // Max dimension 1024px
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.8 // 80% quality
  };

  try {
    toast.info('Зураг боловсруулж байна...', { autoClose: 2000 });
    
    // Compress each file
    const compressedFiles = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`📸 Processing ${i + 1}/${files.length}: ${file.name}`);
      console.log(`   Original size: ${(file.size / 1024).toFixed(2)}KB`);
      
      // Dynamic import browser-image-compression
      const imageCompression = (await import('browser-image-compression')).default;
      const compressedFile = await imageCompression(file, options);
      
      console.log(`   ✅ Compressed size: ${(compressedFile.size / 1024).toFixed(2)}KB`);
      console.log(`   📉 Reduction: ${(((file.size - compressedFile.size) / file.size) * 100).toFixed(1)}%`);
      
      compressedFiles.push(compressedFile);
    }

    console.log('✅ All files compressed successfully');

    // Хуучин зургуудтай нэмж хадгалах
    const newSelectedImages = [...selectedImages, ...compressedFiles];
    console.log('📦 Setting selectedImages:', newSelectedImages.length);
    setSelectedImages(newSelectedImages);

    // Preview зурагнууд үүсгэх
    const newPreviews = compressedFiles.map(file => {
      const url = URL.createObjectURL(file);
      console.log('🖼️ Created preview URL:', url);
      return url;
    });
    
    const allPreviews = [...imagePreview, ...newPreviews];
    console.log('📦 Setting imagePreview:', allPreviews.length);
    setImagePreview(allPreviews);
    
    console.log('✅ State updated successfully');
    console.log('🔍 ===== END DEBUG =====');
    
    toast.success(`${files.length} зураг нэмэгдлээ (Нийт: ${allPreviews.length})`);
    
    // Reset input
    e.target.value = '';
    
  } catch (error) {
    console.error('❌ Compression error:', error);
    toast.error('Зураг боловсруулахад алдаа гарлаа');
    e.target.value = '';
  }
};

   const removeImage = (index) => {
    console.log('🗑️ Removing image at index:', index);
    console.log('📊 Before remove - selectedImages:', selectedImages.length);
    console.log('📊 Before remove - imagePreview:', imagePreview.length);
    
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreview.filter((_, i) => i !== index);
    
    console.log('📊 After remove - newImages:', newImages.length);
    console.log('📊 After remove - newPreviews:', newPreviews.length);
    
    setSelectedImages(newImages);
    setImagePreview(newPreviews);
    
    toast.info(`Зураг устгагдлаа. Үлдсэн: ${newPreviews.length}`);
  };
  // ========================================
  // HANDLERS
  // ========================================
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleCategoryFilter = (e) => {
    setSelectedCategory(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination({ ...pagination, limit: newLimit, page: 1 });
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('productsViewMode', mode);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      barcode: '',
      name: '',
      category_id: '',
      unit: 'ШИРХЭГ',
      retail_price: '',
      wholesale_price: '',
      wholesale_quantity: '',
      cost_price: '',
      initial_stock: 0,
      supplier: '',
      description: ''
    });
    setSelectedImages([]);
    setImagePreview([]);
  };

  // ========================================
  // CREATE PRODUCT
  // ========================================
  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add text fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add images
      console.log('🔍 ===== CREATE PRODUCT - FORM DATA =====');
      console.log('📤 selectedImages array:', selectedImages);
      console.log('📤 selectedImages length:', selectedImages.length);
      console.log('📤 selectedImages is Array?', Array.isArray(selectedImages));
      
      if (selectedImages && selectedImages.length > 0) {
        console.log('✅ Adding images to FormData...');
        selectedImages.forEach((image, index) => {
          console.log(`📎 Image ${index + 1}:`, {
            name: image.name,
            size: image.size,
            type: image.type,
            isFile: image instanceof File
          });
          formDataToSend.append('images', image);
        });
        console.log('✅ Images added to FormData');
      } else {
        console.log('⚠️ No images to add');
      }

      // Debug FormData
      console.log('📦 FormData contents:');
      for (let pair of formDataToSend.entries()) {
        if (pair[1] instanceof File) {
          console.log(`  ${pair[0]}: [File] ${pair[1].name}`);
        } else {
          console.log(`  ${pair[0]}: ${pair[1]}`);
        }
      }

      console.log('🔍 ===== SENDING TO BACKEND =====');
      console.log('📦 FormData entries:');
      for (let [key, value] of formDataToSend.entries()) {
        if (value instanceof File) {
          console.log(`  ${key}: [File] ${value.name} (${value.size} bytes)`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }
      console.log('🔍 ===== END =====');

      const response = await axios.post('/api/products', formDataToSend, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        },
        maxContentLength: 10 * 1024 * 1024, // 10MB
        maxBodyLength: 10 * 1024 * 1024 // 10MB
      });

      if (response.data.success) {
        toast.success('Бараа амжилттай бүртгэгдлээ');
        setShowCreateModal(false);
        resetForm();
        fetchProducts();
      }
    } catch (error) {
      console.error('❌ Create error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // EDIT PRODUCT
  // ========================================
  const openEditModal = (product) => {
    console.log('🔍 Opening edit modal for product:', product.id);
    console.log('🖼️ Raw images data:', product.images);
    console.log('🖼️ Image URL:', product.image_url);
    
    setSelectedProduct(product);
    setFormData({
      barcode: product.barcode,
      name: product.name,
      category_id: product.category_id,
      unit: product.unit,
      retail_price: product.retail_price,
      wholesale_price: product.wholesale_price || '',
      wholesale_quantity: product.wholesale_quantity || '',
      cost_price: product.cost_price,
      initial_stock: 0,
      supplier: product.supplier || '',
      description: product.description || ''
    });

    // Existing images preview - RESET
    setImagePreview([]);
    setSelectedImages([]); // Шинэ зураг сонгоогүй гэдэг
    
    if (product.images) {
      try {
        let imageArray = [];
        
        if (typeof product.images === 'string') {
          console.log('📝 Images is STRING, parsing JSON...');
          imageArray = JSON.parse(product.images);
        } else if (Array.isArray(product.images)) {
          console.log('📝 Images is already ARRAY');
          imageArray = product.images;
        }
        
        console.log('✅ Parsed image array:', imageArray);
        
        if (imageArray.length > 0) {
          const previews = imageArray.map(url => `${axios.defaults.baseURL}${url}`);
          console.log('✅ Setting image previews:', previews);
          setImagePreview(previews);
        } else if (product.image_url) {
          console.log('⚠️ No images in array, using image_url');
          setImagePreview([`${axios.defaults.baseURL}${product.image_url}`]);
        }
      } catch (err) {
        console.error('❌ Parse images error:', err);
        if (product.image_url) {
          console.log('🔄 Fallback to image_url');
          setImagePreview([`${axios.defaults.baseURL}${product.image_url}`]);
        }
      }
    } else if (product.image_url) {
      console.log('📝 No images field, using image_url only');
      setImagePreview([`${axios.defaults.baseURL}${product.image_url}`]);
    }

    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add text fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      formDataToSend.append('is_active', selectedProduct.is_active);

      // Add new images (if selected)
      console.log('🔍 ===== EDIT PRODUCT - IMAGES =====');
      console.log('📤 selectedImages length:', selectedImages.length);
      console.log('📤 selectedImages:', selectedImages);
      
      if (selectedImages && selectedImages.length > 0) {
        console.log('✅ Adding NEW images to FormData...');
        selectedImages.forEach((image, index) => {
          console.log(`📎 Image ${index + 1}:`, {
            name: image.name,
            size: image.size,
            type: image.type,
            isFile: image instanceof File
          });
          formDataToSend.append('images', image);
        });
        console.log('✅ New images added');
      } else {
        console.log('📝 No new images selected, backend will keep existing');
      }

      const response = await axios.put(`/api/products/${selectedProduct.id}`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Бараа амжилттай шинэчлэгдлээ');
        setShowEditModal(false);
        resetForm();
        fetchProducts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // DELETE PRODUCT
  // ========================================
  const handleDelete = async (product) => {
    if (!window.confirm(`"${product.name}" барааг устгах уу?`)) return;

    try {
      const response = await axios.delete(`/api/products/${product.id}`);

      if (response.data.success) {
        toast.success('Бараа устгагдлаа');
        fetchProducts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  // ========================================
  // IMPORT EXCEL
  // ========================================
  const handleImport = async (e) => {
    e.preventDefault();

    if (!importFile) {
      toast.warning('Excel файл сонгоно уу');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    setLoading(true);

    try {
      const response = await axios.post('/api/products/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowImportModal(false);
        setImportFile(null);
        fetchProducts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Импорт хийхэд алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // PAGINATION
  // ========================================
  const goToPage = (page) => {
    setPagination({ ...pagination, page });
  };

  // ========================================
  // VIEW DETAILS
  // ========================================
  const openDetailModal = (product) => {
    setSelectedProduct(product);
    setShowDetailModal(true);
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="products-page">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Бараа бүтээгдэхүүн</h1>
            <p className="text-gray-600 mt-1">Барааны мэдээлэл удирдах</p>
          </div>
          
          {/* Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            {/* View Toggle */}
            <div className="btn-group">
              <button
                onClick={() => handleViewModeChange('list')}
                className={`btn-group-item ${viewMode === 'list' ? 'active' : ''}`}
              >
                <FiList />
              </button>
              <button
                onClick={() => handleViewModeChange('grid')}
                className={`btn-group-item ${viewMode === 'grid' ? 'active' : ''}`}
              >
                <FiGrid />
              </button>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              <FiPlus className="mr-2" />
              <span className="hidden sm:inline">Бараа бүртгэх</span>
              <span className="sm:hidden">Бүртгэх</span>
            </button>
            
            <button
              onClick={() => setShowImportModal(true)}
              className="btn btn-secondary"
            >
              <FiUpload className="mr-2" />
              <span className="hidden sm:inline">Import хийх</span>
              <span className="sm:hidden">Import</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Баркод эсвэл нэрээр хайх..."
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={handleCategoryFilter}
                className="input"
              >
                <option value="">Бүх ангилал</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Limit Select */}
            <div>
              <select
                value={pagination.limit}
                onChange={handleLimitChange}
                className="input"
              >
                <option value="20">20 бараа</option>
                <option value="50">50 бараа</option>
                <option value="100">100 бараа</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Products Display */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="spinner-lg"></div>
        </div>
      ) : viewMode === 'list' ? (
        <ProductsList
          products={products}
          onView={openDetailModal}
          onEdit={openEditModal}
          onDelete={handleDelete}
        />
      ) : (
        <ProductsGrid
          products={products}
          onView={openDetailModal}
          onEdit={openEditModal}
          onDelete={handleDelete}
        />
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination-container">
          <p className="pagination-info">
            Нийт {pagination.total} бараа
          </p>
          <div className="pagination-controls">
            <button
              onClick={() => goToPage(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              Өмнөх
            </button>
            <span className="pagination-page-info">
              {pagination.page} / {pagination.pages}
            </span>
            <button
              onClick={() => goToPage(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="pagination-button"
            >
              Дараах
            </button>
          </div>
        </div>
      )}

      {/* MODALS */}
      {showCreateModal && (
        <ProductFormModal
          title="Бараа бүртгэх"
          formData={formData}
          categories={categories}
          loading={loading}
          selectedImages={selectedImages}
          imagePreview={imagePreview}
          onChange={handleFormChange}
          onImageSelect={handleImageSelect}
          onImageRemove={removeImage}
          onSubmit={handleCreate}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          isEdit={false}
        />
      )}

      {showEditModal && (
        <ProductFormModal
          title="Бараа засах"
          formData={formData}
          categories={categories}
          loading={loading}
          selectedImages={selectedImages}
          imagePreview={imagePreview}
          onChange={handleFormChange}
          onImageSelect={handleImageSelect}
          onImageRemove={removeImage}
          onSubmit={handleEdit}
          onClose={() => {
            setShowEditModal(false);
            resetForm();
          }}
          isEdit={true}
        />
      )}

      {showImportModal && (
        <ImportModal
          file={importFile}
          loading={loading}
          onFileChange={(e) => setImportFile(e.target.files[0])}
          onSubmit={handleImport}
          onClose={() => {
            setShowImportModal(false);
            setImportFile(null);
          }}
        />
      )}

      {showDetailModal && selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
};

// ========================================
// PRODUCTS LIST VIEW
// ========================================
const ProductsList = ({ products, onView, onEdit, onDelete }) => (
  <div className="table-container">
    <table className="table">
      <thead className="table-header">
        <tr>
          <th className="table-th">Зураг</th>
          <th className="table-th">Баркод</th>
          <th className="table-th">Нэр</th>
          <th className="table-th">Ангилал</th>
          <th className="table-th text-right">Үнэ</th>
          <th className="table-th text-right">Үлдэгдэл</th>
          <th className="table-th">Төлөв</th>
          <th className="table-th text-right">Үйлдэл</th>
        </tr>
      </thead>
      <tbody>
        {products.length === 0 ? (
          <tr>
            <td colSpan="8" className="text-center py-12 text-gray-500">
              <FiPackage size={48} className="mx-auto mb-3 opacity-50" />
              <p>Бараа олдсонгүй</p>
            </td>
          </tr>
        ) : (
          products.map(product => (
            <tr key={product.id} className="table-row">
              <td className="table-td">
                {product.image_url ? (
                  <img
                    src={`${axios.defaults.baseURL}${product.image_url}`}
                    alt={product.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                    <FiPackage className="text-gray-400" />
                  </div>
                )}
              </td>
              <td className="table-td">
                <span className="font-mono text-sm">{product.barcode}</span>
              </td>
              <td className="table-td">
                <p className="font-semibold text-gray-900">{product.name}</p>
                <p className="text-xs text-gray-500">{product.unit}</p>
              </td>
              <td className="table-td">
                <span className="badge badge-info">{product.category_name}</span>
              </td>
              <td className="table-td text-right">
                <p className="font-semibold text-gray-900">
                  {product.retail_price.toLocaleString()}₮
                </p>
                {product.wholesale_price && (
                  <p className="text-xs text-gray-500">
                    Бөөн: {product.wholesale_price.toLocaleString()}₮
                  </p>
                )}
              </td>
              <td className="table-td text-right">
                <span className={`font-semibold ${
                  product.stock_quantity < 10 ? 'text-red-600' : 'text-gray-900'
                }`}>
                  {product.stock_quantity}
                </span>
              </td>
              <td className="table-td">
                <span className={`badge ${
                  product.is_active ? 'badge-success' : 'badge-gray'
                }`}>
                  {product.is_active ? 'Идэвхтэй' : 'Идэвхгүй'}
                </span>
              </td>
              <td className="table-td text-right">
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => onView(product)}
                    className="text-blue-600 hover:text-blue-700"
                    title="Дэлгэрэнгүй"
                  >
                    <FiEye size={18} />
                  </button>
                  <button
                    onClick={() => onEdit(product)}
                    className="text-green-600 hover:text-green-700"
                    title="Засах"
                  >
                    <FiEdit size={18} />
                  </button>
                  <button
                    onClick={() => onDelete(product)}
                    className="text-red-600 hover:text-red-700"
                    title="Устгах"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ========================================
// PRODUCTS GRID VIEW
// ========================================
const ProductsGrid = ({ products, onView, onEdit, onDelete }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
    {products.length === 0 ? (
      <div className="col-span-full text-center py-12 text-gray-500">
        <FiPackage size={48} className="mx-auto mb-3 opacity-50" />
        <p>Бараа олдсонгүй</p>
      </div>
    ) : (
      products.map(product => (
        <div key={product.id} className="card hover:shadow-lg transition-shadow">
          {/* Image */}
          <div className="relative mb-3">
            {product.image_url ? (
              <img
                src={`${axios.defaults.baseURL}${product.image_url}`}
                alt={product.name}
                className="w-full h-48 object-cover rounded-lg"
              />
            ) : (
              <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                <FiPackage size={48} className="text-gray-400" />
              </div>
            )}
            
            {/* Status Badge */}
            <span className={`absolute top-2 right-2 badge ${
              product.is_active ? 'badge-success' : 'badge-gray'
            }`}>
              {product.is_active ? 'Идэвхтэй' : 'Идэвхгүй'}
            </span>

            {/* Low Stock Warning */}
            {product.stock_quantity < 10 && (
              <span className="absolute top-2 left-2 badge badge-danger">
                Бага үлдэгдэл
              </span>
            )}
          </div>

          {/* Product Info */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">{product.barcode}</p>
            <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
              {product.name}
            </h3>
            <span className="badge badge-info text-xs">
              {product.category_name}
            </span>
          </div>

          {/* Pricing */}
          <div className="mb-3 pb-3 border-b">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-gray-600">Үнэ:</span>
              <span className="text-lg font-bold text-primary-600">
                {product.retail_price.toLocaleString()}₮
              </span>
            </div>
            {product.wholesale_price && (
              <div className="flex items-baseline justify-between mt-1">
                <span className="text-xs text-gray-600">Бөөн:</span>
                <span className="text-sm font-semibold text-gray-700">
                  {product.wholesale_price.toLocaleString()}₮
                </span>
              </div>
            )}
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-xs text-gray-600">Үлдэгдэл:</span>
              <span className={`text-sm font-semibold ${
                product.stock_quantity < 10 ? 'text-red-600' : 'text-gray-900'
              }`}>
                {product.stock_quantity} {product.unit}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => onView(product)}
              className="flex-1 btn btn-sm btn-secondary"
            >
              <FiEye className="mr-1" size={14} />
              Харах
            </button>
            <button
              onClick={() => onEdit(product)}
              className="btn btn-sm text-green-600 hover:bg-green-50"
            >
              <FiEdit size={16} />
            </button>
            <button
              onClick={() => onDelete(product)}
              className="btn btn-sm text-red-600 hover:bg-red-50"
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        </div>
      ))
    )}
  </div>
);

// ========================================
// PRODUCT FORM MODAL
// ========================================
const ProductFormModal = ({ 
  title, 
  formData, 
  categories, 
  loading, 
  selectedImages,
  imagePreview,
  onChange, 
  onImageSelect,
  onImageRemove,
  onSubmit, 
  onClose, 
  isEdit 
}) => (
  <div className="modal-overlay">
    <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto">
      <div className="modal-header sticky top-0 bg-white z-10">
        <h3 className="modal-title">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FiX size={24} />
        </button>
      </div>

      <form onSubmit={onSubmit}>
        <div className="modal-body">
          {/* Image Upload Section */}
          <div className="mb-6 pb-6 border-b">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FiImage className="inline mr-1" />
              Барааны зураг (хамгийн ихдээ 3, бүр нь 2MB хүртэл)
            </label>
            
            {/* Image Preview */}
            {imagePreview.length > 0 && (
              <div className="grid grid-cols-3 gap-4 mb-4">
                {imagePreview.map((preview, index) => (
                  <div key={index} className="relative">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => onImageRemove(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-lg"
                      title="Зураг устгах"
                    >
                      <FiX size={16} />
                    </button>
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isEdit && imagePreview.length > 0 && selectedImages.length === 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  💡 Хуучин зургийг хадгалах бол шинэ зураг сонгох шаардлагагүй. Шинэ зураг оруулбал хуучин зураг солигдоно.
                </p>
              </div>
            )}

            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <FiUpload className="w-8 h-8 mb-2 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">
                      Зураг сонгох (хамгийн ихдээ 3)
                    </span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Одоогоор: {imagePreview.length} зураг сонгогдсон
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG, WEBP (MAX. 2MB each)</p>
                </div>
                <input
                  id="product-images-input"
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/jpg,image/webp"
                  multiple
                  onChange={onImageSelect}
                />
              </label>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Баркод */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Баркод <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="barcode"
                value={formData.barcode}
                onChange={onChange}
                className="input"
                required
              />
            </div>

            {/* Нэр */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Барааны нэр <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={onChange}
                className="input"
                required
              />
            </div>

            {/* Ангилал */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Ангилал <span className="text-red-500">*</span>
              </label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={onChange}
                className="input"
                required
              >
                <option value="">Сонгох...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Хэмжих нэгж */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Хэмжих нэгж <span className="text-red-500">*</span>
              </label>
              <select
                name="unit"
                value={formData.unit}
                onChange={onChange}
                className="input"
                required
              >
                <option value="ШИРХЭГ">ШИРХЭГ</option>
                <option value="КГ">КГ</option>
                <option value="ЛИТР">ЛИТР</option>
                <option value="МЕТР">МЕТР</option>
                <option value="ХАЙРЦАГ">ХАЙРЦАГ</option>
              </select>
            </div>

            {/* Жижиглэн үнэ */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Жижиглэн үнэ <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="retail_price"
                value={formData.retail_price}
                onChange={onChange}
                className="input"
                min="0"
                step="0.01"
                required
              />
            </div>

            {/* Өртөг */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Өртөг <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="cost_price"
                value={formData.cost_price}
                onChange={onChange}
                className="input"
                min="0"
                step="0.01"
                required
              />
            </div>

            {/* Бөөн үнэ */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Бөөн үнэ
              </label>
              <input
                type="number"
                name="wholesale_price"
                value={formData.wholesale_price}
                onChange={onChange}
                className="input"
                min="0"
                step="0.01"
              />
            </div>

            {/* Бөөн тоо */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Бөөн тоо
              </label>
              <input
                type="number"
                name="wholesale_quantity"
                value={formData.wholesale_quantity}
                onChange={onChange}
                className="input"
                min="0"
              />
            </div>

            {/* Анхны үлдэгдэл */}
            {!isEdit && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Анхны үлдэгдэл
                </label>
                <input
                  type="number"
                  name="initial_stock"
                  value={formData.initial_stock}
                  onChange={onChange}
                  className="input"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Автоматаар орлого баримт үүснэ
                </p>
              </div>
            )}

            {/* Нийлүүлэгч */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Нийлүүлэгч
              </label>
              <input
                type="text"
                name="supplier"
                value={formData.supplier}
                onChange={onChange}
                className="input"
              />
            </div>

            {/* Тайлбар */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Тайлбар
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={onChange}
                className="input"
                rows="3"
              ></textarea>
            </div>
          </div>
        </div>

        <div className="modal-footer sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Болих
          </button>
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? (
              <>
                <div className="spinner mr-2"></div>
                Хадгалж байна...
              </>
            ) : (
              <>
                <FiSave className="mr-2" />
                Хадгалах
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  </div>
);

// ========================================
// IMPORT MODAL
// ========================================
const ImportModal = ({ file, loading, onFileChange, onSubmit, onClose }) => (
  <div className="modal-overlay">
    <div className="modal-content max-w-xl">
      <div className="modal-header">
        <h3 className="modal-title">Excel импорт</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FiX size={24} />
        </button>
      </div>

      <form onSubmit={onSubmit}>
        <div className="modal-body">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-semibold text-blue-800 mb-2">Excel загвар:</p>
            <div className="text-xs text-gray-700 space-y-1">
              <p>• <strong>Баркод</strong> - Барааны баркод (заавал)</p>
              <p>• <strong>Барааны нэр</strong> - Нэр (заавал)</p>
              <p>• <strong>Үнэ</strong> - Жижиглэн үнэ (заавал)</p>
              <p>• <strong>Бүлэг</strong> - Ангилал (заавал)</p>
              <p>• <strong>Хэмжих нэгж</strong> - ШИРХЭГ, КГ гэх мэт</p>
              <p>• <strong>Өртөг</strong> - Өртгийн үнэ</p>
              <p>• <strong>Тоо ширхэг</strong> - Анхны үлдэгдэл</p>
            </div>
          </div>

          {/* File Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Excel файл сонгох (.xlsx, .xls)
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={onFileChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100 cursor-pointer"
            />
            {file && (
              <p className="text-sm text-gray-600 mt-2">
                Сонгогдсон: <strong>{file.name}</strong>
              </p>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Болих
          </button>
          <button type="submit" disabled={loading || !file} className="btn btn-primary">
            {loading ? (
              <>
                <div className="spinner mr-2"></div>
                Импорт хийж байна...
              </>
            ) : (
              <>
                <FiUpload className="mr-2" />
                Импорт хийх
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  </div>
);

// ========================================
// PRODUCT DETAIL MODAL
// ========================================
const ProductDetailModal = ({ product, onClose }) => {
  // Selected image state
  const [selectedImage, setSelectedImage] = React.useState(0);
  
  console.log('🔍 ProductDetailModal - Product ID:', product.id);
  console.log('🖼️ Raw images:', product.images);
  console.log('🖼️ Image URL:', product.image_url);
  
  // Parse images safely
  let images = [];
  
  try {
    if (product.images) {
      if (typeof product.images === 'string') {
        console.log('📝 Parsing JSON string...');
        images = JSON.parse(product.images);
      } else if (Array.isArray(product.images)) {
        console.log('📝 Already an array');
        images = product.images;
      }
    }
    
    console.log('✅ Parsed images array:', images);
    
    if (images.length === 0 && product.image_url) {
      console.log('⚠️ Empty array, using image_url');
      images = [product.image_url];
    }
  } catch (err) {
    console.error('❌ Parse error:', err);
    if (product.image_url) {
      console.log('🔄 Fallback to image_url');
      images = [product.image_url];
    }
  }
  
  console.log('🎯 Final images to display:', images);

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-3xl">
        <div className="modal-header">
          <h3 className="modal-title">Барааны дэлгэрэнгүй</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Images */}
            <div>
            {images.length > 0 ? (
              <div className="space-y-3">
                {/* Main Image */}
                <div className="relative">
                  <img
                    src={`${axios.defaults.baseURL}${images[selectedImage]}`}
                    alt={product.name}
                    className="w-full h-80 object-cover rounded-lg border-2 border-gray-200"
                  />
                  
                  {/* Image Counter Badge */}
                  {images.length > 1 && (
                    <div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white text-xs px-3 py-1.5 rounded-full">
                      {selectedImage + 1} / {images.length}
                    </div>
                  )}
                </div>
                
                {/* Thumbnail Gallery - Доод тал */}
                {images.length > 1 && (
                  <div className="grid grid-cols-3 gap-2">
                    {images.map((img, idx) => (
                      <img
                        key={idx}
                        src={`${axios.defaults.baseURL}${img}`}
                        alt={`${product.name} ${idx + 1}`}
                        onClick={() => setSelectedImage(idx)}
                        className={`w-full h-24 object-cover rounded-lg cursor-pointer border-2 transition-all ${
                          selectedImage === idx 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
                </div>
              ) : (
                <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                  <FiPackage size={64} className="text-gray-400" />
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Баркод</p>
                <p className="font-mono font-semibold text-gray-900">{product.barcode}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Нэр</p>
                <p className="font-semibold text-gray-900">{product.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Ангилал</p>
                <span className="badge badge-info">{product.category_name}</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Хэмжих нэгж</p>
                <p className="font-semibold text-gray-900">{product.unit}</p>
              </div>
            </div>

            {/* Prices */}
            <div className="md:col-span-2 grid grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Жижиглэн үнэ</p>
                <p className="text-xl font-bold text-blue-600">
                  {product.retail_price.toLocaleString()}₮
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Өртөг</p>
                <p className="text-xl font-bold text-green-600">
                  {product.cost_price.toLocaleString()}₮
                </p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Бөөн үнэ</p>
                <p className="text-xl font-bold text-yellow-600">
                  {product.wholesale_price 
                    ? `${product.wholesale_price.toLocaleString()}₮`
                    : '-'
                  }
                </p>
                {product.wholesale_quantity && (
                  <p className="text-xs text-gray-600 mt-1">
                    {product.wholesale_quantity}+ ширхэг
                  </p>
                )}
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Үлдэгдэл</p>
                <p className="text-xl font-bold text-purple-600">
                  {product.stock_quantity}
                </p>
              </div>
            </div>

            {/* Additional Info */}
            {product.supplier && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-600">Нийлүүлэгч</p>
                <p className="font-semibold text-gray-900">{product.supplier}</p>
              </div>
            )}

            {product.description && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-600">Тайлбар</p>
                <p className="text-gray-900">{product.description}</p>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary w-full">
            Хаах
          </button>
        </div>
      </div>
    </div>
  );
};

export default Products;